import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Trophy, Gift, Ticket, CheckCircle2, XCircle, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/** Small badge that shows per-prize eligibility for the current signed-in user. */
function EligibilityBadge({ elig }) {
  if (!elig) return null;
  if (elig.eligible) {
    return (
      <span
        data-testid={`prize-eligible-${elig.prize_id}`}
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-primary/15 border border-primary/40 text-primary"
      >
        <CheckCircle2 size={12}/> You&apos;re eligible
      </span>
    );
  }
  if (elig.will_be_eligible_by_draw && elig.will_be_eligible_on) {
    return (
      <span
        data-testid={`prize-pending-${elig.prize_id}`}
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-secondary/15 border border-secondary/40 text-secondary"
      >
        <CalendarClock size={12}/> You&apos;ll turn {elig.min_age} on{" "}
        {new Date(elig.will_be_eligible_on).toLocaleDateString(undefined, { month: "short", day: "numeric" })} — eligible by draw
      </span>
    );
  }
  const daysMsg = elig.days_until_eligible != null
    ? ` (${elig.days_until_eligible} day${elig.days_until_eligible === 1 ? "" : "s"} to go)`
    : "";
  const cashMsg = elig.cash_alternative_arv
    ? ` · $${elig.cash_alternative_arv} cash alternative available`
    : "";
  return (
    <span
      data-testid={`prize-ineligible-${elig.prize_id}`}
      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/5 border border-white/20 text-white/60"
      title={`Winner must be ${elig.min_age}+${cashMsg}`}
    >
      <XCircle size={12}/> Age {elig.min_age}+{daysMsg}
    </span>
  );
}

export default function Prizes() {
  const { user } = useAuth();
  const [prizes, setPrizes] = useState([]);
  const [eligMap, setEligMap] = useState({});
  useEffect(() => { api.get("/prizes").then(r=>setPrizes(r.data)); }, []);
  useEffect(() => {
    if (!user || user.role === "business") return;
    api.get("/consumer/prizes/eligibility")
      .then(r => {
        const map = {};
        (r.data.prizes || []).forEach(p => { map[p.prize_id] = p; });
        setEligMap(map);
      })
      .catch(() => { /* silent — public page still renders */ });
  }, [user]);
  const grand = prizes.filter(p => p.prize_type === "grand");
  const rest = prizes.filter(p => p.prize_type !== "grand");
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <Seo
        title="Prizes — Cancun Grand Prize + Daily Drawings"
        description="Full list of ADN Local FanFest 2026 prizes: a 6-day / 5-night Cancun trip for two with roundtrip airfare + $500 cash (ARV $2,999), $100 restaurant certificates, Jacksonville hotel stays, and $50 sponsor gift cards. Total ARV approximately $4,899. Free to enter."
      />
      <div className="text-xs tracking-[0.3em] uppercase text-primary">Prize Vault</div>
      <h1 className="font-bebas text-5xl mt-2">EVERY PRIZE. EVERY DAY.</h1>
      <div className="mt-8 space-y-6">
        {grand.map(p => (
          <div key={p.id} className="grand-card p-8 flex flex-col sm:flex-row items-start gap-6">
            <div className="w-14 h-14 rounded bg-secondary/20 text-secondary flex items-center justify-center"><Trophy /></div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.3em] uppercase purple-text">Grand Prize</div>
              <div className="font-bebas text-4xl gold-text">{p.name}</div>
              <p className="text-white/80 mt-2">{p.description}</p>
              <div className="mt-3 text-xs text-white/60 flex flex-wrap gap-4">
                <span>ARV: ${p.approximate_retail_value}</span>
                <span>Draw: {p.scheduled_draw_date}</span>
                <span>Claim by: {p.claim_deadline}</span>
                <span className="text-secondary font-semibold">Age: {p.min_age || 21}+</span>
                {p.cash_alternative_arv ? (
                  <span className="text-white/70">Cash alt: ${p.cash_alternative_arv}</span>
                ) : null}
              </div>
              <EligibilityBadge elig={eligMap[p.id]} />
            </div>
          </div>
        ))}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rest.map(p => (
            <div key={p.id} className="adn-card p-6">
              <div className="w-12 h-12 rounded bg-primary/15 text-primary flex items-center justify-center mb-3">
                {p.prize_type === "certificate" ? <Ticket/> : <Gift/>}
              </div>
              <div className="font-bebas text-2xl">{p.name}</div>
              <p className="text-white/70 text-sm mt-1">{p.description}</p>
              <div className="mt-2 text-xs text-white/60 flex flex-wrap items-center gap-2">
                <span>Qty {p.quantity} · ARV ${p.approximate_retail_value}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                  (p.min_age || 18) >= 21
                    ? "border-secondary/40 text-secondary bg-secondary/10"
                    : "border-primary/40 text-primary bg-primary/10"
                }`}>{p.min_age || 18}+</span>
              </div>
              {p.cash_alternative_arv ? (
                <div className="mt-1 text-[11px] text-white/50">
                  Cash alternative available: ${p.cash_alternative_arv}
                </div>
              ) : null}
              <EligibilityBadge elig={eligMap[p.id]} />
            </div>
          ))}
        </div>
      </div>

      <section className="mt-14 adn-card p-6">
        <h2 className="font-bebas text-2xl">CLAIM INSTRUCTIONS &amp; AGE ELIGIBILITY</h2>
        <ul className="mt-3 list-disc list-inside text-white/70 text-sm space-y-1">
          <li>Sweepstakes entry is open to Jacksonville-area residents <b className="text-primary">18 and older</b>. One entry per family / household.</li>
          <li><b className="text-secondary">21+ prizes</b> (Grand Prize Cancun trip, hotel weekend stays, and any prize including alcohol or lodging): winner must present government-issued ID showing age 21 or older to claim.</li>
          <li><b className="text-primary">18+ prizes</b> (gift cards, restaurant certificates, and other non-travel awards): open to all registered entrants aged 18 or older.</li>
          <li><b className="gold-text">Cash Alternative Clause:</b> If a selected winner cannot accept the intended travel or lodging prize (e.g., does not meet the venue&apos;s age requirement or cannot travel), ADN Local will award a <b>cash amount equal to the prize&apos;s Approximate Retail Value</b> in lieu of the travel/lodging.</li>
          <li>Winners are notified by email and/or SMS within 48 hours. You have until the listed claim deadline to respond and provide verification.</li>
          <li>Prizes are non-transferable. Winners are responsible for applicable federal, state, and local taxes.</li>
          <li>Travel prizes subject to blackout dates and certificate rules. Unclaimed prizes will be awarded to an alternate winner.</li>
        </ul>
      </section>
    </div>
  );
}
